import React, { useState } from 'react';
import { Result, Button, Typography, Card, Tag, message } from 'antd';
import { CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const RouteDebugPage = ({
  type = 'not-found', // 'not-found' | 'access-denied' | 'component-missing'
  path = '',
  routeName = '',
  debugInfo = {}
}) => {
  const [copied, setCopied] = useState(false);

  const statusConfig = {
    'not-found': { status: '404', title: 'Route Not Found', color: '#faad14' },
    'access-denied': { status: '403', title: 'Access Denied', color: '#ff4d4f' },
    'component-missing': { status: 'warning', title: 'Component Missing', color: '#fa8c16' },
  };

  const config = statusConfig[type] || statusConfig['not-found'];

  const fullDebugInfo = {
    type,
    path,
    routeName,
    timestamp: new Date().toISOString(),
    ...debugInfo,
  };

  const handleCopy = () => {
    const text = JSON.stringify(fullDebugInfo, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      message.success('Debug info copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      message.success('Debug info copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderDebugRow = (label, value) => {
    if (value === undefined || value === null) return null;
    const boolColor = value === true ? 'green' : value === false ? 'red' : 'default';
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong style={{ fontSize: 13 }}>{label}</Text>
        {typeof value === 'boolean' ? (
          <Tag color={boolColor} icon={value ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
            {String(value)}
          </Tag>
        ) : (
          <Text code style={{ fontSize: 12 }}>{String(value)}</Text>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: '20px' }}>
      <div style={{ maxWidth: 600, width: '100%' }}>
        <Result
          status={config.status}
          title={config.title}
          subTitle={
            type === 'component-missing'
              ? `Route "${routeName || path}" is configured but its componentPath is missing in routeTabConfig.js.`
              : type === 'access-denied'
              ? `You don't have permission to access "${routeName || path}".`
              : `Route "${path}" was not found.`
          }
        />

        <Card
          size="small"
          title="Debug Info"
          extra={
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleCopy}
              type={copied ? 'primary' : 'default'}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          }
          style={{ marginTop: -20 }}
        >
          {renderDebugRow('Route Path', path)}
          {renderDebugRow('Route Name', routeName)}
          {renderDebugRow('Has componentPath', debugInfo.hasComponentPath)}
          {renderDebugRow('In RBAC Config', debugInfo.inRbacConfig)}
          {renderDebugRow('componentPath', debugInfo.componentPath)}
          {renderDebugRow('Error', debugInfo.error)}
          {renderDebugRow('rbacEnabled', debugInfo.rbacEnabled)}
          {renderDebugRow('allowed', debugInfo.allowed)}
          {renderDebugRow('Permission Group', debugInfo.permissionGroup)}

          {type === 'component-missing' && (
            <Paragraph style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
              <WarningOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
              To fix: Add <Text code>componentPath</Text> to this route in <Text code>src/config/routeTabConfig.js</Text> and create the component file.
            </Paragraph>
          )}
          {debugInfo.hint && (
            <Paragraph style={{ marginTop: 8, fontSize: 13, color: '#999' }}>
              {debugInfo.hint}
            </Paragraph>
          )}
        </Card>
      </div>
    </div>
  );
};

export default RouteDebugPage;
